import { useEffect, useState, useRef } from 'react';
import { OVERLAY_PANEL_GAP } from '../../pages/Shoreline/theme';
import { useDispatch, useSelector } from 'react-redux';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { Actions as Imagery } from '../../../store/ducks/imagery';
import { Box, Card, Button, IconButton } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SimpleInfoDialog from '../dialog/SimpleInfoDialog';
import { useStepFlow, useBaselineDrawing, useTransectsConfig } from '../../../hooks';
import ShapeEditControl from '../map/ShapeEditControl';
import SelectImagesStep from './SelectImagesStep';
import DrawBaselineStep from './DrawBaselineStep';
import GenerateTransectsStep from './GenerateTransectsStep';

/**
 *
 * @function AnalysisSteps
 * @description
 * Component that manages the steps for drawing transects on a map and performing analysis.
 * @param {Object} props
 * @param {Function} props.t - Translation function for internationalization.
 * @param {Object} props.mapRef - Reference to the Google Maps instance.
 * @param {Object} props.roiCoords - Coordinates of the region of interest polygon.
 * @param {Object} props.bounds - Bounds of the map view.
 * @param {Object} props.drawingManager - Reference to the Google Maps Drawing Manager instance.
 * @param {Array} [props.initialBaselineCoords] - Optional pre-drawn baseline ([lng, lat] pairs) to
 * restore on mount Drawn once; the user still confirms it through the usual DRAW_BASELINE dialog.
 * **Called by:
 * @see {ProcessingPage} - Shoreline's result page (Shoreline/result.jsx).
 * @see {CSqueezeResult} - CSqueeze's result page.
 * @see {EUSCoastResult} - EUSCoast's result page.
 * @returns {JSX.Element} Coastline Analysis steps JSX element
 */
const AnalysisSteps = ({
  title = 'forms.imageChooser.actions.analyzeShoreline.title',
  steps = ['ANALYSIS_DONE'],
  t,
  mapRef,
  roiCoords,
  drawingManager,
  initialBaselineCoords,
  onAnalysisPerform,
  onRestartAnalysis = () => {},
}) => {
  const { step, next, restart, previous, isLast } = useStepFlow(steps);
  const errorAnalysis = useSelector((state) => state.imagery.error);
  const baseline = useBaselineDrawing({
    mapRef,
    roiCoords,
    drawingManager,
    t,
    onConfirmed: () => {},
    initialBaseLineCoords: initialBaselineCoords,
  });
  const transects = useTransectsConfig({ baseLineCoords: baseline.baseLineCoords, mapRef, t });
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const overlayRef = useRef(null);
  const dispatch = useDispatch();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();

  useEffect(() => {
    if (!errorAnalysis) return;
    notifyError(t('forms.shorelineAnalysis.errors.invalidTransectError'));
    restartAnalysis();
  }, [errorAnalysis]);

  /**
   * The drawn baseline goes along with its coordinates. A module that only measures along it needs
   * the coordinates alone - Shoreline takes four arguments and ignores this one - but a module that
   * keeps showing it afterwards needs the overlay itself: to toggle its visibility, to write it into
   * an export, to hand it to a Shapefile. CSqueeze asked for it and was never given it, so its
   * baseline was missing from every export it produced and the button that hides it did nothing.
   */
  useEffect(() => {
    if (step != 'ANALYSIS_DONE') return;
    /**
     * The preview goes before the real transects arrive. Both are polylines over the same baseline,
     * so leaving it on would draw every transect twice - once in the colour of its result, once in
     * the purple that only ever meant "this is where they will be".
     */
    transects.clearTransectsPreview();
    onAnalysisPerform(
      baseline.baseLineCoords,
      transects.transectsStep,
      transects.transectsExtent,
      transects.threshold,
      baseline.baseLine,
    );
  }, [step]);

  /**
   * @function restartAnalysis
   * @description
   * Set the default value to all variables so the user can restaart the analysis. Removes drawn
   * overlays (baseline and transects) and clears active listeners from the DrawingManager.
   * **Called by:
   * @see {SelectImagesStep} - via the "Restart analysis" button's onClick, and the errorAnalysis effect.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const restartAnalysis = () => {
    restart();
    onRestartAnalysis();
    dispatch(Imagery.restartAnalysis());
    transects.setTransectsExtent(0);
    transects.setTransectsStep(0);
    baseline.setBaseLineCoords([]);
    baseline.baseLine?.overlay?.setMap?.(null);
    baseline.editing.setShape(null);
    baseline.setBaseLine(null);
    baseline.setConfirmDrawing(false);
    transects.transectsObjects.forEach((object) => object.setMap(null));
    transects.setTransectsObjects([]);
    if (drawingManager.current) {
      window.google.maps.event.clearInstanceListeners(drawingManager.current);
      drawingManager.current.setMap(null);
      drawingManager.current = null;
    }
  };

  return (
    <Box
      position="relative"
      ref={overlayRef}
      style={{ display: 'flex', flexDirection: 'column', gap: OVERLAY_PANEL_GAP }}>
      <ShapeEditControl editing={baseline.editing} visible={baseline.confirmDrawing} />
      {isLast ? (
        <Card style={{ padding: 12 }} elevation={3}>
          <Button
            color="secondary"
            variant="outlined"
            style={{
              width: '100%',
              height: 35,
              padding: 'inherit 20px',
            }}
            onClick={restartAnalysis}>
            {t('forms.shorelineAnalysis.restartAnalysis').toUpperCase()}
          </Button>
        </Card>
      ) : (
        <Card
          style={{
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
          elevation={3}>
          <div
            style={{
              fontWeight: 'bold',
              borderBottom: '1px solid gray',
              paddingBottom: 5,
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
            }}>
            {t(title)}
            <IconButton onClick={() => setShowHelpDialog(true)} color="primary" size="small" style={{ marginLeft: 8 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </div>
          <SimpleInfoDialog
            open={showHelpDialog}
            close={() => setShowHelpDialog(false)}
            title={t('forms.transectEvolution.title')}
            text={t('forms.transectEvolution.TransectEvolutionInfoDialog')} //DIFERENTE ENTRE MÓDULOS
          />
          <Box
            id="steps_bt_container"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
            {/**
             * Every step is on the list, all the time. Only the one whose turn it is can be used -
             * each control disables itself when it is not current - which is how this panel worked
             * before, and what the people using it asked to have back: seeing what the analysis is
             * going to ask for, and how far along it is, beats being shown one instruction at a
             * time with the rest out of sight.
             */}
            {steps.includes('SELECT_IMAGES') && (
              <SelectImagesStep t={t} onNext={() => next()} isCurrent={step === 'SELECT_IMAGES'} />
            )}
            {steps.includes('DRAW_BASELINE') && (
              <DrawBaselineStep
                t={t}
                {...baseline}
                onNext={() => next()}
                isCurrent={step === 'DRAW_BASELINE'}
                overlayTarget={overlayRef}
              />
            )}
            {steps.includes('CONFIGURE_TRANSECTS') && (
              <GenerateTransectsStep
                t={t}
                {...transects}
                onPrevious={() => {
                  baseline.setBaseLineCoords([]);
                  baseline.baseLine?.overlay?.setMap?.(null);
                  baseline.setBaseLine(null);
                  baseline.setConfirmDrawing(false);
                  previous();
                }}
                onNext={() => next()}
                isCurrent={step === 'CONFIGURE_TRANSECTS'}
                overlayTarget={overlayRef}
              />
            )}
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default AnalysisSteps;
